import '../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useState } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { IoClose } from 'react-icons/io5';
import { 
    FaXTwitter, 
    FaInstagram, 
    FaFacebook, 
    FaLinkedin, 
    FaGithub, 
    FaMedium,
    FaDev,
    FaYoutube,
    FaTiktok,
    FaTwitch,
    FaBehance,
    FaTelegram,
    FaCirclePlus,
    FaCopy
} from 'react-icons/fa6';
import { PRIMARY_COLOR, PALE_WHITE_4, PALE_WHITE_1, WHITE, PRIMARY_COLOR_4, GREEN } from '../../appearance/Colors';
import customAnalytics from '../../util/custom-analytics';
import { AuthContext } from '../../util/context';

const SOCIAL_NETWORKS = [
    // Copy option - First Row
    {
        id: 'copy',
        name: 'Copy',
        Icon: FaCopy,
        color: PRIMARY_COLOR_4,
        backgroundColor: PALE_WHITE_1,
        isCopy: true
    },
    // Most Popular - Row 1
    { 
        id: 'x', 
        name: 'X', 
        Icon: FaXTwitter,
        color: '#000000'
    },
    { 
        id: 'instagram', 
        name: 'Instagram', 
        Icon: FaInstagram, 
        color: '#E1306C' 
    },
    { 
        id: 'facebook', 
        name: 'Facebook', 
        Icon: FaFacebook, 
        color: '#4267B2' 
    },
    { 
        id: 'linkedin', 
        name: 'LinkedIn', 
        Icon: FaLinkedin, 
        color: '#0077B5' 
    },
    // Professional - Row 2
    { 
        id: 'github', 
        name: 'GitHub', 
        Icon: FaGithub, 
        color: '#333' 
    },
    { 
        id: 'medium', 
        name: 'Medium', 
        Icon: FaMedium, 
        color: '#00AB6C' 
    },
    { 
        id: 'dev', 
        name: 'Dev.to', 
        Icon: FaDev,
        color: '#0A0A0A' 
    },
    // Content Creation - Row 3
    { 
        id: 'youtube', 
        name: 'YouTube', 
        Icon: FaYoutube, 
        color: '#FF0000' 
    },
    { 
        id: 'tiktok', 
        name: 'TikTok', 
        Icon: FaTiktok,
        color: '#000000' 
    },
    { 
        id: 'twitch', 
        name: 'Twitch', 
        Icon: FaTwitch, 
        color: '#9146FF' 
    },
    { 
        id: 'behance', 
        name: 'Behance', 
        Icon: FaBehance, 
        color: '#1769FF' 
    },
    // Social/Communication - Row 4
    { 
        id: 'telegram', 
        name: 'Telegram', 
        Icon: FaTelegram, 
        color: '#0088cc' 
    },
    // Request More Button
    { 
        id: 'request', 
        name: 'Request More', 
        Icon: FaCirclePlus,
        color: PRIMARY_COLOR_4,
        backgroundColor: PALE_WHITE_1,
        isRequest: true
    },
];

const customStyles = {
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
    },
    content: {
        position: 'relative',
        top: 'auto',
        left: 'auto',
        right: 'auto',
        bottom: 'auto',
        maxWidth: '600px',
        width: '90%',
        padding: 0,
        border: 'none',
        background: 'none',
        maxHeight: '80vh',
        zIndex: 10000
    }
};

const SocialNetworkModal = ({ isOpen, onClose, username }) => {
    const {t: lnT} = useTranslation();

    const { context: { setShowPaperclip } } = React.useContext(AuthContext);
    const [copiedUsername, setCopiedUsername] = useState(false);
    const [copiedNetworkId, setCopiedNetworkId] = useState(null);

    const handleNetworkClick = (e, network) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (network.isCopy) {
            navigator.clipboard.writeText(username).then(() => {
                setCopiedUsername(true);
                setCopiedNetworkId(network.id);
                setTimeout(() => {
                    setCopiedUsername(false);
                    setCopiedNetworkId(null);
                }, 1500);
                customAnalytics.message_action({
                    "action": "Copy",
                    "type": "Username"
                });
            });
            return;
        }

        if (network.isRequest) {
            customAnalytics.message_action({
                "action": "Request",
                "type": "Social Network"
            });
            setShowPaperclip(true);
        } else {
            let url;
            switch (network.id) {
                case 'x':
                    url = `https://x.com/${username}`;
                    break;
                case 'instagram':
                    url = `https://instagram.com/${username}`;
                    break;
                case 'facebook':
                    url = `https://facebook.com/${username}`;
                    break;
                case 'linkedin':
                    url = `https://linkedin.com/in/${username}`;
                    break;
                case 'github':
                    url = `https://github.com/${username}`;
                    break;
                case 'medium':
                    url = `https://medium.com/@${username}`;
                    break;
                case 'dev':
                    url = `https://dev.to/${username}`;
                    break;
                case 'youtube':
                    url = `https://youtube.com/@${username}`;
                    break;
                case 'tiktok':
                    url = `https://tiktok.com/@${username}`;
                    break;
                case 'twitch':
                    url = `https://twitch.tv/${username}`;
                    break;
                case 'behance':
                    url = `https://behance.net/${username}`;
                    break;
                case 'telegram':
                    url = `https://t.me/${username}`;
                    break;
            }
            if (url) {
                window.open(url, '_blank');
                customAnalytics.message_action({
                    "action": "Open",
                    "type": "Social",
                    "network": network.id,
                    "username": username
                });
            }
        }
    };

    const handleNetworkLongClick = (e, network) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!network.isRequest) {
            let url;
            switch (network.id) {
                case 'x':
                    url = `https://x.com/${username}`;
                    break;
                case 'instagram':
                    url = `https://instagram.com/${username}`;
                    break;
                case 'facebook':
                    url = `https://facebook.com/${username}`;
                    break;
                case 'linkedin':
                    url = `https://linkedin.com/in/${username}`;
                    break;
                case 'github':
                    url = `https://github.com/${username}`;
                    break;
                case 'medium':
                    url = `https://medium.com/@${username}`;
                    break;
                case 'dev':
                    url = `https://dev.to/${username}`;
                    break;
                case 'youtube':
                    url = `https://youtube.com/@${username}`;
                    break;
                case 'tiktok':
                    url = `https://tiktok.com/@${username}`;
                    break;
                case 'twitch':
                    url = `https://twitch.tv/${username}`;
                    break;
                case 'behance':
                    url = `https://behance.net/${username}`;
                    break;
                case 'telegram':
                    url = `https://t.me/${username}`;
                    break;
            }
            if (url) {
                window.open(url, '_blank');
                customAnalytics.message_action({
                    "action": "Open",
                    "type": "Social",
                    "network": network.id,
                    "username": username
                });
            }
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            style={customStyles}
            contentLabel={lnT('messages:socialNetworkModal')}
            shouldCloseOnOverlayClick={true}
            onClick={(e) => e.stopPropagation()}
        >
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <Header>
                    <HeaderText>{lnT('common:open_9a546be4')}{username}{lnT('common:on')}</HeaderText>
                    <CloseButton onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}>
                        <IoClose size={20} color={PALE_WHITE_4} />
                    </CloseButton>
                </Header>
                <NetworksGrid onClick={(e) => e.stopPropagation()}>
                    {SOCIAL_NETWORKS.map((network) => (
                        <NetworkItem
                            key={network.id}
                            onClick={(e) => handleNetworkClick(e, network)}
                            onContextMenu={(e) => handleNetworkLongClick(e, network)}
                        >
                            <IconContainer style={{ 
                                backgroundColor: copiedNetworkId === network.id ? GREEN : (network.backgroundColor || network.color)
                            }}>
                                <network.Icon size={28} color={WHITE} />
                            </IconContainer>
                            <NetworkName style={{
                                color: copiedNetworkId === network.id ? GREEN : PALE_WHITE_4
                            }}>
                                {network.isCopy && copiedNetworkId === network.id ? lnT('messages:copied_8d525e5f') : network.name}
                            </NetworkName>
                        </NetworkItem>
                    ))}
                </NetworksGrid>
            </ModalContent>
        </Modal>
    );
};

const ModalContent = styled.div`
    background-color: ${PRIMARY_COLOR};
    border-radius: 16px;
    overflow: hidden;
    max-height: 80vh;
`;

const Header = styled.div`
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const HeaderText = styled.span`
    color: ${PALE_WHITE_4};
    font-weight: 600;
    text-transform: uppercase;
    font-size: 14px;
`;

const CloseButton = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 16px;
    background-color: ${PALE_WHITE_1};
    border: none;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    padding: 0;
`;

const NetworksGrid = styled.div`
    padding: 2px 16px 16px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 16px;
    max-height: calc(80vh - 64px);
    overflow-y: auto;

    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
        display: none;
    }
    
    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
`;

const NetworkItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    transition: transform 0.2s;

    &:hover {
        transform: translateY(-2px);
    }
`;

const IconContainer = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 28px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 6px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    position: relative;
    transition: all 0.2s ease;
`;

const NetworkName = styled.span`
    color: ${PALE_WHITE_4};
    font-size: 12px;
    text-align: center;
    font-weight: 500;
    transition: all 0.2s ease;
`;

export default SocialNetworkModal; 